import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

export default function OrganisationSettings({ organisation, onUpdate }) {
  const [formData, setFormData] = useState({
    name: '',
    timezone: '',
    locale: 'en-US'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (organisation) {
      setFormData({
        name: organisation.name || '',
        timezone: organisation.timezone || 'UTC',
        locale: organisation.locale || 'en-US'
      });
    }
  }, [organisation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const updates = {};
      if (formData.name !== organisation.name) {
        updates.name = formData.name;
      }
      if (formData.timezone !== organisation.timezone) {
        updates.timezone = formData.timezone;
      }
      if (formData.locale !== organisation.locale) {
        updates.locale = formData.locale;
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setLoading(false);
        return;
      }

      const updated = await authService.updateOrganisation(updates);
      setSuccess(true);
      
      // Notify parent component
      if (onUpdate) {
        onUpdate(updated);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update organisation settings');
    } finally {
      setLoading(false);
    }
  };

  // Common timezones
  const timezones = [
    'UTC',
    'Asia/Bangkok',
    'Asia/Singapore',
    'Asia/Hong_Kong',
    'Asia/Kuala_Lumpur',
    'Asia/Jakarta',
    'Asia/Manila',
    'Asia/Ho_Chi_Minh',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'America/New_York',
    'America/Los_Angeles',
    'America/Chicago',
    'America/Toronto',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Pacific/Auckland'
  ];

  // Common locales
  const locales = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'th-TH', label: 'Thai' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'zh-TW', label: 'Chinese (Traditional)' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'ms-MY', label: 'Malay' },
    { value: 'id-ID', label: 'Indonesian' },
    { value: 'vi-VN', label: 'Vietnamese' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' }
  ];

  if (!organisation) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <p className="text-gray-500">Loading organisation settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Organisation Settings</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm">Settings updated successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organisation Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Organisation Display Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            placeholder="Enter organisation name"
          />
        </div>

        {/* Timezone */}
        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
            <span className="text-gray-500 text-xs ml-2">(Used for scheduling daily processing)</span>
          </label>
          <select
            id="timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {/* Locale/Language */}
        <div>
          <label htmlFor="locale" className="block text-sm font-medium text-gray-700 mb-2">
            Default Locale/Language
          </label>
          <select
            id="locale"
            name="locale"
            value={formData.locale}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
          >
            {locales.map(loc => (
              <option key={loc.value} value={loc.value}>{loc.label}</option>
            ))}
          </select>
        </div>

        {/* Data Residency Region (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data Residency Region
            <span className="text-gray-500 text-xs ml-2">(Not editable)</span>
          </label>
          <input
            type="text"
            value={organisation.data_residency_region || 'APAC'}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">
            Data residency region cannot be changed once the organisation is active.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
